import React from 'react';
import { blogPosts } from '@/data/seoBlog';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { ChevronRight, Calendar, ArrowLeft, BookOpen, BrainCircuit, Atom, Sigma, Beaker, Code2, Calculator, Landmark, Briefcase, Code, Network } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';

export const BlogPage = ({ onNavigate, slug }: { onNavigate: (path: string) => void, slug?: string }) => {
  const getCategoryIcon = (category: string) => {
    switch (true) {
      case category.includes('Physics'): return <Atom className="w-6 h-6" />;
      case category.includes('Biology'): return <BrainCircuit className="w-6 h-6" />;
      case category.includes('Chemistry'): return <Beaker className="w-6 h-6" />;
      case category.includes('Mathematics'): return <Sigma className="w-6 h-6" />;
      case category.includes('Computer'): return <Code2 className="w-6 h-6" />;
      case category.includes('Engineering'): return <Network className="w-6 h-6" />;
      case category.includes('Commerce'): return <Briefcase className="w-6 h-6" />;
      case category.includes('UPSC'): return <Landmark className="w-6 h-6" />;
      case category.includes('Reasoning'): return <BrainCircuit className="w-6 h-6" />;
      case category.includes('Aptitude'): return <Calculator className="w-6 h-6" />;
      default: return <BookOpen className="w-6 h-6" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (true) {
      case category.includes('Physics'): return 'from-blue-500/20 to-cyan-500/5 text-cyan-400 border-cyan-500/20';
      case category.includes('Biology'): return 'from-emerald-500/20 to-teal-500/5 text-emerald-400 border-emerald-500/20';
      case category.includes('Chemistry'): return 'from-rose-500/20 to-pink-500/5 text-rose-400 border-rose-500/20';
      case category.includes('Mathematics'): return 'from-indigo-500/20 to-blue-500/5 text-indigo-400 border-indigo-500/20';
      case category.includes('Computer'): return 'from-violet-500/20 to-purple-500/5 text-violet-400 border-violet-500/20';
      case category.includes('Engineering'): return 'from-amber-500/20 to-orange-500/5 text-amber-400 border-amber-500/20';
      case category.includes('UPSC'): return 'from-yellow-500/20 to-amber-500/5 text-yellow-400 border-yellow-500/20';
      default: return 'from-slate-500/20 to-slate-400/5 text-slate-300 border-slate-500/20';
    }
  };

  if (slug) {
    const post = blogPosts.find(p => p.slug === slug);
    if (!post) return <div className="text-white p-12 text-center text-xl">Blog not found</div>;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "datePublished": post.date,
      "author": {
        "@type": "Organization",
        "name": "Professor X AI",
        "url": "https://professorxai.info/"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Professor X AI",
        "url": "https://professorxai.info/",
        "logo": "https://professorxai.info/profxlogo.png"
      }
    };

    const colorClasses = getCategoryColor(post.category);

    return (
      <>
        <SEO 
          title={`${post.title} | Professor X AI Blog`}
          description={post.excerpt}
          canonical={`https://professorxai.info/blog/${slug}`}
          type="article"
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        
        <div className="max-w-4xl mx-auto px-6 py-12 text-slate-200">
          <button onClick={() => onNavigate('/blog')} className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-8 transition-colors">
            <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
              <ArrowLeft className="w-4 h-4" />
            </div>
            Back to Blog
          </button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={cn("bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/5 rounded-[2rem] p-8 md:p-14 shadow-2xl relative overflow-hidden", `before:absolute before:inset-0 before:bg-gradient-to-br before:opacity-10 before:pointer-events-none before:${colorClasses.split(' ')[0]}`)}>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2" />
            <div className="flex flex-wrap items-center gap-4 mb-8 relative z-10">
              <div className={cn("flex flex-row items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-br border uppercase tracking-wider text-xs font-bold shadow-lg", colorClasses)}>
                {getCategoryIcon(post.category)}
                {post.category}
              </div>
              <span className="text-slate-400 text-sm flex items-center gap-2 font-medium bg-white/5 px-4 py-1.5 rounded-full border border-white/5">
                <Calendar className="w-4 h-4"/> {post.date}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-10 leading-tight tracking-tight relative z-10 drop-shadow-sm">{post.title}</h1>
            <div className="prose prose-invert prose-lg max-w-none text-slate-300 relative z-10 prose-headings:text-white prose-headings:font-bold prose-h2:mt-12 prose-h2:mb-6 prose-p:leading-relaxed prose-a:text-indigo-400 prose-li:my-1">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Educational Blog & Learning Guides | Professor X AI"
        description="Read the latest educational tips, exam strategies, subject guides, and study notes for JEE, NEET, CAT, CBSE, and Engineering subjects."
        canonical="https://professorxai.info/blog"
      />
      <div className="max-w-[1700px] mx-auto px-6 py-12 text-slate-200">
        <button 
          onClick={() => onNavigate('/')}
          className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-12 transition-colors relative z-10"
        >
          <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back to Home
        </button>

        <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           className="mb-20 text-center relative z-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-sm uppercase tracking-widest mb-6 shadow-sm">
            Educational Resources
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight drop-shadow-lg">
            Educational Blog
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">Latest strategies, smart tips, and high-yield topics to help you ace your competitive exams with top ranks.</p>
          
          <div className="mt-12 flex justify-center">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-indigo-500/30 p-1 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 shadow-[0_0_30px_rgba(79,70,229,0.2)] mx-auto relative z-10">
              <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
            </div>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-[1600px] mx-auto relative z-10">
          {blogPosts.map((post, idx) => {
             const colorClasses = getCategoryColor(post.category);
             return (
             <motion.div 
               key={post.slug}
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: idx * 0.05 }}
               onClick={() => onNavigate(`/blog/${post.slug}`)}
               className="bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-8 cursor-pointer hover:border-white/20 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,0,0,0.5)] hover:-translate-y-1 flex flex-col h-full group overflow-hidden relative"
             >
               <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none", colorClasses.split(' ')[0], colorClasses.split(' ')[1])} />
               <div className={cn("flex flex-row items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-br border uppercase tracking-wider text-xs font-bold mb-6 w-fit", colorClasses)}>
                 {getCategoryIcon(post.category)}
                 {post.category}
               </div>
               <h2 className="text-2xl font-bold text-white mb-4 group-hover:text-indigo-300 transition-colors line-clamp-2 leading-tight relative z-10">
                 {post.title}
               </h2>
               <p className="text-slate-400 text-[15px] line-clamp-3 mb-8 flex-1 leading-relaxed relative z-10">
                 {post.excerpt}
               </p>
               <div className="flex items-center justify-between text-sm w-full border-t border-white/5 pt-6 mt-auto relative z-10">
                 <span className="text-slate-500 flex items-center gap-1.5"><Calendar className="w-4 h-4"/> {post.date}</span>
                 <span className="flex items-center text-indigo-400 font-bold group-hover:text-indigo-300 transition-colors">Read <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
               </div>
             </motion.div>
             )
          })}
        </div>
      </div>
    </>
  );
};
